import api from '@/api';
import router from '@/router';
import { useAuthStore } from '@/stores/auth';
import { goToLogin } from '@/helpers/authRedirect';

const IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000;
const CHECK_INTERVAL_MS = 15 * 1000;
const KEEPALIVE_INTERVAL_MS = 10 * 60 * 1000;
const ACTIVITY_THROTTLE_MS = 1000;

const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'wheel',
  'scroll',
  'input',
] as const;

/**
 * Track real user activity and log out after 2 hours without it.
 * A single short interval is used instead of resetting a 2-hour timeout on
 * every mouse move: browsers defer those long timers until the next click,
 * and storing the timer id while resetting it on mousemove made the logout
 * fire while the user was still working.
 */
export function startIdleLogout(): () => void {
  const authStore = useAuthStore();
  let lastActivity = Date.now();
  let lastKeepAlive = Date.now();
  let loggingOut = false;

  const cleanups: Array<() => void> = [];
  const boundDocs = new WeakSet<Document>();
  const boundIframes = new WeakSet<HTMLIFrameElement>();

  const listenerOptions: AddEventListenerOptions = { capture: true, passive: true };

  const shouldLogout = (now: number) => {
    if (loggingOut || !authStore.logged || router.currentRoute.value.name === 'login') {
      return false;
    }

    return now - lastActivity >= IDLE_TIMEOUT_MS;
  };

  const logoutFromIdle = async () => {
    if (loggingOut || !shouldLogout(Date.now())) {
      return;
    }

    loggingOut = true;
    const returnPath = router.currentRoute.value.fullPath;

    try {
      await authStore.logout();
    } catch {
      // The server session may already be gone.
    } finally {
      goToLogin(returnPath);
      window.setTimeout(() => {
        loggingOut = false;
      }, 2000);
    }
  };

  const onActivity = (event: Event) => {
    if (event.isTrusted === false || loggingOut) {
      return;
    }

    const now = Date.now();

    if (shouldLogout(now)) {
      void logoutFromIdle();
      return;
    }

    if (now - lastActivity < ACTIVITY_THROTTLE_MS) {
      return;
    }

    lastActivity = now;
    bindOpenIframes();
  };

  const bindDocument = (doc: Document) => {
    if (boundDocs.has(doc)) {
      return;
    }

    boundDocs.add(doc);

    ACTIVITY_EVENTS.forEach((eventName) => {
      doc.addEventListener(eventName, onActivity, listenerOptions);
    });

    cleanups.push(() => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        doc.removeEventListener(eventName, onActivity, listenerOptions);
      });
    });
  };

  const watchIframe = (iframe: HTMLIFrameElement) => {
    if (boundIframes.has(iframe)) {
      return;
    }

    boundIframes.add(iframe);

    const onLoad = () => {
      try {
        if (iframe.contentDocument) {
          bindDocument(iframe.contentDocument);
        }
      } catch {
        // Cross-origin frames do not expose their document.
      }
    };

    onLoad();
    iframe.addEventListener('load', onLoad);
    cleanups.push(() => iframe.removeEventListener('load', onLoad));
  };

  const bindOpenIframes = () => {
    document.querySelectorAll('iframe').forEach((node) => {
      watchIframe(node as HTMLIFrameElement);
    });
  };

  const keepSessionAlive = async () => {
    if (!authStore.logged || loggingOut) {
      return;
    }

    lastKeepAlive = Date.now();

    try {
      const logged = await api.auth.authCheck();

      if (logged === false) {
        goToLogin(router.currentRoute.value.fullPath);
      }
    } catch {
      // A 401 is handled by the session interceptor, which also sends the user to login.
    }
  };

  const checkIdle = () => {
    if (shouldLogout(Date.now())) {
      void logoutFromIdle();
      return;
    }

    if (!authStore.logged || router.currentRoute.value.name === 'login') {
      return;
    }

    if (Date.now() - lastKeepAlive >= KEEPALIVE_INTERVAL_MS) {
      void keepSessionAlive();
    }
  };

  const onWake = () => {
    bindOpenIframes();
    checkIdle();
  };

  const onWindowBlur = () => {
    window.setTimeout(() => {
      bindOpenIframes();

      // Clicks inside an editor iframe do not reach this window. Bind that
      // document, and if the idle limit already passed, leave immediately.
      if (document.activeElement instanceof HTMLIFrameElement && shouldLogout(Date.now())) {
        void logoutFromIdle();
      }
    }, 0);
  };

  bindDocument(document);
  bindOpenIframes();

  const intervalId = window.setInterval(checkIdle, CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', onWake);
  window.addEventListener('focus', onWake);
  window.addEventListener('pageshow', onWake);
  window.addEventListener('blur', onWindowBlur);

  return () => {
    loggingOut = true;
    window.clearInterval(intervalId);
    document.removeEventListener('visibilitychange', onWake);
    window.removeEventListener('focus', onWake);
    window.removeEventListener('pageshow', onWake);
    window.removeEventListener('blur', onWindowBlur);
    cleanups.splice(0).forEach((cleanup) => cleanup());
  };
}
